import React, { useEffect, useImperativeHandle } from "react";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { uniq } from "ramda";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import { createFundWatchData } from "@hsl/fc-subscription/lib";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import useFirstFocus from "../../../../../hooks/useFirstFocus";
import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import EnvelopeOpenIcon from "../../../../icons/EnvelopeOpen";
import { Card } from "../../Card";
import { DetailFormInput, InvalidInput } from "../AddDetails/FormComponents";

type Props = {
    controlRef: React.RefObject<{ submit: () => Promise<void> }>;
};
const AddRecipient = ({ controlRef }: Props) => {
    const { inputFieldEvent, clickLinkEvent, buttonClickEvent, pageLoadEvent } =
        useYardActionEvents();
    const handleFocus = useFirstFocus<string>((name) => {
        inputFieldEvent({
            name,
        });
    });
    const tx = useTranslation();
    const getStepData = useFCSubscriptionStore(
        (state) => state.getFlowStepData,
    );
    const setStepData = useFCSubscriptionStore(
        (state) => state.setFlowStepData,
    );
    const stepData = useFCSubscriptionStore(
        (state) => state.flowSteps["add-recipient"],
    );
    const sharingMode = useFCSubscriptionStore((state) => state.sharingMode);
    const shareSubscriptions = useFCSubscriptionStore(
        (state) => state.shareSubscriptions,
    );
    const hasEmails = Boolean(
        stepData.data?.emails && stepData.data.emails.length > 0,
    );
    const txIncompleteMsg = tx(
        stepData?.incompleteMsg ?? "Please add at least one recipient",
    );
    const txEmailRequired = tx("Please provide an email");
    const txEmailInvalid = tx("Please provide a valid email");
    const txEmailPlaceholder = tx("Email address");
    const txMsgPlaceholder = tx("Message (optional)");
    const linkBtnClasses =
        "block text-base font-medium text-l-g-blue my-4 pl-10";

    const form = useForm<{ email?: string; message: string }>();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const handleAddAnother = async () => {
        await form.handleSubmit(({ email, message }) => {
            const emails = getStepData("add-recipient")?.data?.emails ?? [];
            if (!email) {
                form.setError("email", { message: txEmailRequired.result });
            } else {
                form.resetField("email");
                form.setFocus("email");
                setStepData("add-recipient", {
                    data: {
                        emails: uniq(emails.concat(email)),
                        message,
                    },
                    complete: true,
                });
            }
        })();
        clickLinkEvent({
            name: "Add another person",
        });
    };

    const handleClearAll = () => {
        setStepData("add-recipient", {
            data: {
                emails: [],
                message: stepData.data?.message,
            },
            complete: false,
        });
    };

    const handleRemoveEmail = (email: string) => {
        const emails = (stepData.data?.emails ?? []).filter((e) => e !== email);
        const message = stepData.data?.message;
        setStepData("add-recipient", {
            data: {
                emails,
                message,
            },
            complete: emails.length > 0,
        });
    };

    useImperativeHandle(controlRef, () => ({
        async submit() {
            const step = stepData;
            const emails = step.data?.emails ?? [];
            await form.handleSubmit(
                async ({ email, message: _message }) => {
                    const updatedEmails = email
                        ? uniq(emails.concat(email))
                        : emails;
                    const complete = updatedEmails.length > 0;
                    const message = _message ?? step.data?.message;
                    setStepData("add-recipient", {
                        data: {
                            emails: updatedEmails,
                            message,
                        },
                        complete: complete,
                    });
                    if (!complete) {
                        throw new Error(txIncompleteMsg.result);
                    }
                    if (sharingMode === "watch") {
                        const subscriptions =
                            getStepData("select-funds")?.data?.funds.map(
                                createFundWatchData,
                            );
                        if (subscriptions && subscriptions.length > 0) {
                            await shareSubscriptions(
                                updatedEmails.map((recipientEmail) => ({
                                    recipientEmail,
                                    message,
                                    subscriptions,
                                })),
                            );
                            const searchParams = new URLSearchParams(params);
                            searchParams.delete("step");
                            const searchStr = searchParams.toString();
                            navigate(
                                `/my-funds${searchStr ? `?${searchStr}` : ""}`,
                            );
                        }
                    }
                },
                (errors) => {
                    throw new Error(errors.email?.message);
                },
            )();
            return await Promise.resolve();
        },
    }));

    useEffect(() => {
        pageLoadEvent({ pageName: "Add recipient email" });
    }, []);
    return (
        <Card className="space-y-8">
            <form onSubmit={(evt) => evt.preventDefault()}>
                <h2 className="mb-2 text-lg font-medium">
                    <Translate>Add recipient email</Translate>
                </h2>
                <p className="my-2">
                    <Translate>
                        Please provide the email of the person you would like to
                        share this fund with
                    </Translate>
                    .
                </p>
                <DetailFormInput icon={<EnvelopeOpenIcon />} className="mt-4">
                    <input
                        type="email"
                        autoComplete="email"
                        placeholder={txEmailPlaceholder.result}
                        onFocus={() => handleFocus("email", "Email")}
                        {...form.register("email", {
                            required: {
                                value: stepData.data?.emails.length === 0,
                                message: txEmailRequired.result,
                            },
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: txEmailInvalid.result,
                            },
                        })}
                    />
                    <InvalidInput error={form.formState.errors.email} />
                </DetailFormInput>
                <button
                    type="button"
                    onClick={handleAddAnother}
                    className={linkBtnClasses}
                >
                    <span className="text-xl font-normal">+</span>{" "}
                    <span className="underline">
                        <Translate>Add another person</Translate>
                    </span>
                </button>
                {stepData?.data?.emails.map((email) => (
                    <p
                        className="text-medium my-2 flex items-center gap-1 pl-10 text-base font-medium"
                        key={email}
                    >
                        <span className="pii">{email}</span>{" "}
                        <button
                            type="button"
                            className="inline cursor-pointer appearance-none"
                            onClick={() => handleRemoveEmail(email)}
                        >
                            <XMarkIcon width={20} height={20} />
                        </button>
                    </p>
                ))}
                {hasEmails && (
                    <button
                        type="button"
                        className={cx(linkBtnClasses, "underline")}
                        onClick={handleClearAll}
                    >
                        <Translate>Clear all</Translate>
                    </button>
                )}

                <div className="flex w-full items-center gap-2 font-medium transition-opacity [&_hr]:flex-1">
                    <hr />
                </div>
                <div className="mt-8 flex flex-col gap-8">
                    <textarea
                        placeholder={txMsgPlaceholder.result}
                        className="border-taupe-grey w-full rounded-sm border px-4 py-2.5"
                        onFocus={() => handleFocus("msg", "Message")}
                        rows={6}
                        {...form.register("message")}
                    />
                </div>
            </form>
        </Card>
    );
};

export default AddRecipient;
